import { useQuery } from '@tanstack/react-query';
import { getResource, apiFetch } from '@diggens/api-client';

export interface RawData {
  id: string;
  [key: string]: unknown;
}

/**
 * Fetch a single raw_data record by ID
 */
export function useRawData(id: string | undefined) {
  return useQuery<RawData | null>({
    queryKey: ['raw_data', id],
    queryFn: () => (id ? getResource<RawData>('raw_data', id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

/**
 * Fetch all raw_data sources linked to a node (via source_raw_data_id on node and PMN children)
 */
export function useRawDataSources(nodeId: string | undefined) {
  return useQuery<RawData[]>({
    queryKey: ['raw_data_sources', nodeId],
    queryFn: () => (nodeId ? apiFetch<RawData[]>(`/nodes/${encodeURIComponent(nodeId)}/raw-data`) : Promise.resolve([])),
    enabled: !!nodeId,
  });
}

/**
 * Fetch potential raw_data matches for a node by name
 * Useful for discovering enrichment data that could apply to existing nodes
 */
export function useRawDataMatches(nodeId: string | undefined, nodeType: string | undefined, nodeName: string | undefined) {
  return useQuery<RawData[]>({
    queryKey: ['raw_data_matches', nodeId, nodeType, nodeName],
    queryFn: () => {
      if (!nodeId || !nodeType || !nodeName) return Promise.resolve([]);
      return apiFetch<RawData[]>(`/nodes/${encodeURIComponent(nodeId)}/raw-data-matches?node_type=${encodeURIComponent(nodeType)}&node_name=${encodeURIComponent(nodeName)}`);
    },
    enabled: !!nodeId && !!nodeType && !!nodeName,
  });
}
